import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { fetchErrorMessageTranslationBundle } from "../api";
import { transformErrorMessageBundleToKeyValue } from "../utils/translation";
import { getLocalizedApiErrorMessage } from "../utils/error";

/**
 * Maps an i18next language to the locale the backend serves catalogs under.
 *
 * The two disagree by design: i18next resources here are registered under bare
 * language codes (`de`, `fr`) so `?lng=fr` works, while skalio-id's
 * `I18nResource.getSupportedLocales()` and skp-common's catalogs use full
 * locales (`de_DE`, `fr_FR`). Requesting the wrong form returns 404, and the
 * failure is silent -- the error text simply stays English.
 *
 * This was previously a special case for German alone
 * (`"de" -> "de_" + "DE".toUpperCase()`), so every other language 404'd.
 * An explicit table rather than string arithmetic, because the region is not
 * derivable from the language: `en` is `en_US`, `ja` is `ja_JP`, `zh` is
 * `zh_CN` -- doubling the language would be wrong for all three.
 *
 * Keep in step with `machine`/`manual` in skp-common's `.l10n.json` and with
 * `getSupportedLocales()`.
 */
const API_LOCALE_BY_LANGUAGE: Record<string, string> = {
  en: "en_US",
  de: "de_DE",
  fr: "fr_FR",
  es: "es_ES",
};

function apiLocaleFor(language: string): string {
  // Already region-qualified ("fr-FR", "fr_FR")? Normalise the separator.
  if (/[-_]/.test(language)) return language.replace(/-/g, "_");
  return API_LOCALE_BY_LANGUAGE[language] ?? language;
}

export const ERROR_MESSAGE_TRANSLATIONS_NS = "errorMessagetranslations";

export const ERROR_MESSAGE_BUNDLE_QUERY_KEY = ["ErrorMessageBundle"] as const;

/**
 * Loads Skalio ID error message translations into i18n (`errorMessagetranslations`).
 * Shared by ApiException surfaces and transfer blocked states so the bundle is fetched once.
 *
 * Skips the network request when the namespace is already registered for the active i18n language
 * (e.g. after an earlier error in the same session). `queryKey` includes the language so a locale
 * change can still fetch the right bundle when it is missing.
 */
export function useErrorMessageTranslationBundleQuery() {
  const { i18n } = useTranslation();
  const language = i18n.language;

  const bundleAlreadyLoaded = i18n.hasResourceBundle(
    language,
    ERROR_MESSAGE_TRANSLATIONS_NS
  );

  return useQuery({
    queryKey: [...ERROR_MESSAGE_BUNDLE_QUERY_KEY, language] as const,
    enabled: !bundleAlreadyLoaded,
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: async () =>
      fetchErrorMessageTranslationBundle(apiLocaleFor(language)),
    onSuccess: (data) => {
      if (!data) return;

      const errorTranslations = transformErrorMessageBundleToKeyValue(data);
      i18n.addResourceBundle(
        i18n.language,
        ERROR_MESSAGE_TRANSLATIONS_NS,
        errorTranslations,
        true,
        false
      );
    },
  });
}

/**
 * The `t` of the server-provided error translation namespace. Always use this
 * instead of an inline `useTranslation("errorMessagetranslations")`: the
 * translation extractor assigns a file's namespace to every literal key in
 * that file, so plain UI strings next to an inline hook silently drop out of
 * the main catalog.
 */
export function useApiErrorTranslation() {
  const { t } = useTranslation(ERROR_MESSAGE_TRANSLATIONS_NS);
  return t;
}

/**
 * Same localization pipeline as {@link ApiExceptionErrorPage} `Content`: bundle first,
 * then map server error text through the error translation namespace.
 */
export function useLocalizedApiErrorText(rawMessage: string): {
  isLoading: boolean;
  localized: string;
} {
  const { isLoading } = useErrorMessageTranslationBundleQuery();
  const t = useApiErrorTranslation();

  return {
    isLoading,
    localized: getLocalizedApiErrorMessage(rawMessage, t),
  };
}
